import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios, { AxiosError } from 'axios'

/**
 * Trigger the orchestrator that pushes a deal to Heartbeat (same as
 * `scripts/heartbeat/test-deal-submission-push.ts`). Two production-
 * supported callers exist:
 *
 *   - The no-signature "Create Contract" flow on the deal page calls this
 *     (with trigger='no_signature_flow') after the contract lands in
 *     stage='Deal Won' / status='Completed Internally', because such
 *     deals never produce a PandaDoc webhook to drive the push.
 *   - The dev push button in the Heartbeat Preview tab calls this
 *     (with trigger='dev_button'). The button is hidden in production
 *     via `isHeartbeatDevPushEnabled()` below.
 *
 * Callers SHOULD pass an explicit `trigger` so the push timeline labels
 * the row correctly; the backend treats a missing trigger as 'unknown'.
 *
 * On success the deal-preview query is invalidated so the live snapshot +
 * subscription overlay re-render with whatever the push wrote.
 */

export type SubmitDealTrigger = 'no_signature_flow' | 'dev_button' | 'cli'

export interface SubmitDealResultPerService {
  success: boolean
  submissionId?: number
  submissionType?: string
  status?: number
  body?: unknown
  error?: string
}

export interface SubmitDealResult {
  flow: 'A' | 'B' | 'IO' | 'none'
  success: boolean
  emittedSubmissions: number
  predecessorSource:
    | 'predecessorId'
    | 'fallback-master-agreement'
    | 'fallback-same-type'
    | 'fallback-latest-active'
    | 'none'
  predecessorDealSk?: string
  skipReason?: string
  /** Present with skipReason='invalid_product_pricing'. */
  pricingProblems?: Array<{ serviceLine: string; detail: string }>
  accountSource?: 'already_linked' | 'created' | 'matched_existing'
  parentAccountCreated?: boolean
  submitterHeartbeatUserId?: number | null
  teamMembersAttached?: number
  teamMembersSkippedUnknownUser?: number
  parentTeamMembersAttached?: number
  parentTeamMembersSkippedUnknownUser?: number
  results: SubmitDealResultPerService[]
  contractResult?: {
    success: boolean
    contractId?: number
    subscriptionIds?: number[]
    error?: string
  }
}

export interface SubmitDealResponse {
  success: true
  result: SubmitDealResult
}

export interface SubmitDealError {
  success: false
  code: string
  error: string
}

export interface SubmitDealVariables {
  PK: string
  SK: string
  submittedBy?: string
  pandaDocId?: string
  signedAt?: string
  trigger?: SubmitDealTrigger
}

const submitDeal = async ({
  PK,
  SK,
  submittedBy,
  pandaDocId,
  signedAt,
  trigger,
}: SubmitDealVariables): Promise<SubmitDealResponse> => {
  const response = await axios.post<SubmitDealResponse>(
    `${baseUrl}/aws/heartbeat/deal/${encodeURIComponent(PK)}/${encodeURIComponent(SK)}/submit`,
    { submittedBy, pandaDocId, signedAt, trigger },
  )
  return response.data
}

export const useSubmitDealToHeartbeat = () => {
  const queryClient = useQueryClient()
  return useMutation<SubmitDealResponse, AxiosError<SubmitDealError>, SubmitDealVariables>({
    mutationFn: submitDeal,
    onSuccess: (_data, vars) => {
      // Re-fetch the preview + org snapshot so the live overlay reflects
      // whatever just got written.
      queryClient.invalidateQueries({ queryKey: ['deal-heartbeat-preview', vars.PK, vars.SK] })
      queryClient.invalidateQueries({ queryKey: ['org-heartbeat-snapshot'] })
    },
  })
}

/** True when the dev-only push button should be visible. Mirrors the
 *  backend gate so the UI doesn't show a button that would 404. */
export const isHeartbeatDevPushEnabled = (): boolean => {
  // VITE_ENV mirrors NODE_ENV in our build. The flag is set in
  // .env.production but not in .env.local, so this returns false in prod
  // and true everywhere else.
  return import.meta.env.VITE_ENV !== 'production'
}
